import React, { useState } from 'react';
import { Edit, Trash2, ImageIcon, Package, Eye, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Product {
  _id?: string;
  id?: string;
  slug?: { tr: string; en: string; es: string };
  categoryId?: string;
  title?: { tr: string; en: string; es: string };
  summary?: { tr: string; en: string; es: string };
  content?: {
    product_description?: { tr: string; en: string; es: string };
    technical_areas?: { tr: string; en: string; es: string };
    usage_areas?: { tr: string; en: string; es: string };
    usage_instructions?: { tr: string; en: string; es: string };
    advantages?: {
      tr?: string[];
      en?: string[];
      es?: string[];
    };
  };
  specs?: {
    [key: string]: string;
  };
  subProducts?: Array<{ productId: string }>;
  seo?: {
    metaTitle?: { tr: string; en: string; es: string };
    metaDescription?: { tr: string; en: string; es: string };
  };
  image?: string;
  images?: string[];
  order?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ProductTableProps {
  products: Product[];
  loading: boolean;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onUpdateOrder?: (productId: string, order: number) => void;
  onAddSubProduct?: (product: Product) => void;
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  loading,
  onEdit,
  onDelete,
  onUpdateOrder,
  onAddSubProduct,
}) => {
  const navigate = useNavigate();
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [tempOrderValues, setTempOrderValues] = useState<{ [key: string]: number }>({});

  const handleOrderChange = (productId: string, value: number) => {
    setTempOrderValues(prev => ({ ...prev, [productId]: value }));
  };

  const handleSaveOrder = (productId: string) => {
    if (onUpdateOrder && tempOrderValues[productId] !== undefined) {
      onUpdateOrder(productId, tempOrderValues[productId]);
      setEditingOrderId(null);
      // Temizle
      setTempOrderValues(prev => {
        const newValues = { ...prev };
        delete newValues[productId];
        return newValues;
      });
    }
  };

  const handleCancelOrder = (productId: string) => {
    setEditingOrderId(null);
    // Temizle
    setTempOrderValues(prev => {
      const newValues = { ...prev };
      delete newValues[productId];
      return newValues;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Yükleniyor...</span>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Bu kategoride henüz ürün bulunmuyor.
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <table className="w-full table-fixed divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-16 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Görsel
            </th>
            <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ürün Adı
            </th>
            <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Özet
            </th>
            <th className="w-24 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sıra
            </th>
            <th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Özellikler
            </th>
            <th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Oluşturulma
            </th>
            <th className="w-24 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              İşlemler
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {products.map((product) => {
            const productImage = product.images?.[0] || product.image;
            const createdDate = product.createdAt
              ? new Date(product.createdAt).toLocaleDateString('tr-TR')
              : '-';

            return (
              <tr key={product._id || product.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {productImage ? (
                      <img
                        src={productImage}
                        alt="Product"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {product.title?.tr || 'İsimsiz Ürün'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 max-w-48 truncate">
                    {product.summary?.tr || '-'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {onUpdateOrder ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        value={
                          editingOrderId === (product._id || product.id)
                            ? tempOrderValues[product._id || product.id!] ?? product.order ?? 0
                            : product.order ?? 0
                        }
                        onChange={(e) => {
                          const productId = product._id || product.id;
                          if (productId) {
                            setEditingOrderId(productId);
                            handleOrderChange(productId, parseInt(e.target.value) || 0);
                          }
                        }}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {editingOrderId === (product._id || product.id) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const productId = product._id || product.id;
                            if (productId) {
                              handleSaveOrder(productId);
                            }
                          }}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                          title="Kaydet"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-600">{product.order ?? 0}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 max-w-24 truncate">
                    {product.content?.technical_areas?.tr || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {createdDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    {/* Sub-Products Buttons */}
                    {product.subProducts && product.subProducts.length > 0 && (
                      <button
                        onClick={() => navigate(`/sub-products/${product._id || product.id}`)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        title="Alt Ürünleri Görüntüle"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="text-xs">({product.subProducts.length})</span>
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/sub-products/${product._id || product.id}`)}
                      className="text-green-600 hover:text-green-900"
                      title="Alt Ürün Ekle"
                    >
                      <Package className="h-4 w-4" />
                    </button>
                    
                    {/* Edit & Delete */}
                    <button
                      onClick={() => onEdit(product)}
                      className="text-primary-600 hover:text-primary-900"
                      title="Düzenle"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete(product)}
                      className="text-red-600 hover:text-red-900"
                      title="Sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;

